import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Shield, Copy, Check, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { useAuthStore } from '../../store/authStore';

interface MFASetupFormData {
  token: string;
}

interface MFASetupFormProps {
  onSuccess?: (secret: string, authToken?: string) => void;
  onBack?: () => void;
  isRegistration?: boolean;
  isPostLogin?: boolean;
  email?: string; // Required for registration
  password?: string; // Required for post-login
}

interface MFASetupData {
  secret: string;
  qrCode: string;
  message: string;
}

export function MFASetupForm({ onSuccess, onBack, isRegistration = false, isPostLogin = false, email, password }: MFASetupFormProps) {
  const [mfaData, setMfaData] = useState<MFASetupData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [secretCopied, setSecretCopied] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<MFASetupFormData>();
  const { toast } = useToast();
  const authStore = useAuthStore();

  useEffect(() => {
    initializeMFA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeMFA = async () => {
    try {
      setIsInitializing(true);
      let response;
      
      if (isRegistration && email) {
        response = await api.setupMFAForRegistration(email);
      } else if (isPostLogin && email && password) {
        response = await api.setupMFAPostLogin(email, password);
      } else {
        response = await api.setupMFA();
      }
      
      setMfaData(response);
    } catch (error) {
      toast({
        title: "MFA Setup Failed",
        description: error instanceof Error ? error.message : "Failed to initialize MFA setup",
        variant: "destructive",
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const onSubmit = async (data: MFASetupFormData) => {
    if (!mfaData) return;

    try {
      setIsLoading(true);
      
      if (isRegistration) {
        // For registration, verify the token first before completing registration
        await api.verifyMFAForRegistration(mfaData.secret, data.token);
        toast({
          title: "MFA Verified",
          description: "Your authenticator has been successfully verified.",
        });
        onSuccess?.(mfaData.secret);
      } else if (isPostLogin && email && password) {
        // For post-login MFA setup, complete the setup and get auth token
        const response = await api.completeMFAPostLogin(email, password, mfaData.secret, data.token);
        
        // Check auth to update user data in store
        await authStore.checkAuth();
        
        toast({
          title: "MFA Setup Complete",
          description: "Multi-factor authentication has been successfully enabled and you are now logged in.",
        });
        onSuccess?.(mfaData.secret, response.token);
      } else {
        // For normal MFA setup (post-login), enable MFA immediately
        await api.enableMFA(data.token);
        toast({
          title: "MFA Enabled",
          description: "Multi-factor authentication has been successfully enabled on your account.",
        });
        onSuccess?.(mfaData.secret);
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "Invalid verification code",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = async () => {
    if (!mfaData?.secret) return;
    
    try {
      await navigator.clipboard.writeText(mfaData.secret);
      setSecretCopied(true);
      toast({
        title: "Secret Copied",
        description: "The secret key has been copied to your clipboard.",
      });
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Could not copy to clipboard. Please copy manually.",
        variant: "destructive",
      });
    }
  };

  if (isInitializing) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center">
            <Shield className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Setting up MFA...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!mfaData) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-sm text-destructive">Failed to initialize MFA setup</p>
            <Button variant="outline" onClick={initializeMFA} className="mt-2">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
          <Shield className="h-6 w-6" />
          Setup MFA
        </CardTitle>
        <CardDescription className="text-center">
          {isRegistration 
            ? "Secure your account with two-factor authentication" 
            : "Enable two-factor authentication for your account"
          }
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            Step 1: Install Authenticator App
          </h3>
          <p className="text-sm text-muted-foreground">
            Download and install an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="font-semibold">Step 2: Scan QR Code</h3>
          <div className="flex justify-center p-4 bg-muted rounded-lg">
            <img 
              src={mfaData.qrCode} 
              alt="MFA QR Code" 
              className="w-48 h-48"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Scan this QR code with your authenticator app
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="font-semibold">Or enter manually:</h3>
          <div className="flex items-center gap-2">
            <Input 
              value={mfaData.secret} 
              readOnly 
              className="font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copySecret}
              className="shrink-0"
            >
              {secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="token">Step 3: Enter Verification Code</Label>
            <div className="relative">
              <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="token"
                type="text"
                placeholder="000000"
                maxLength={6}
                className="pl-10 text-center tracking-widest"
                {...register('token', {
                  required: 'Verification code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Code must be 6 digits'
                  }
                })}
                autoComplete="one-time-code"
              />
            </div>
            {errors.token && (
              <p className="text-sm text-destructive">{errors.token.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <div className="space-y-2">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : isRegistration ? "Complete Setup" : "Enable MFA"}
            </Button>

            {onBack && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={onBack}
              >
                Back
              </Button>
            )}
          </div>
        </form>

        {isRegistration && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> After completing MFA setup, your registration will be submitted for admin approval. 
              You'll receive an email once approved.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
